import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, TextField, FormHelperText, InputAdornment, IconButton } from '@mui/material';
import { CheckCircle, Person, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { styled as muiStyled } from '@mui/material/styles';
import COLOR from 'utils/constants/color';
import Loading from 'components/Loading';
import styled from 'styled-components';
import { Col, Row } from 'antd';
import { handleAddStudentWithClass } from 'store/users/thunks';
import { lighten } from 'polished';
import  get  from "lodash/get";

const StyledTextField = muiStyled(TextField)(({ theme }) => ({
  '& label': {
    backgroundColor: 'white',
    padding: '0 4px',
  },
  '& label.Mui-focused': {
    backgroundColor: 'white',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'black',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1976d2',
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 100px #fff inset',
      WebkitTextFillColor: '#000',
      caretColor: '#000',
      borderRadius: '4px',
    },
    '& input:-webkit-autofill:hover': {
      WebkitBoxShadow: '0 0 0 100px #fff inset',
    },
    '& input:-webkit-autofill:focus': {
      WebkitBoxShadow: '0 0 0 100px #fff inset',
    },
    '& input:-webkit-autofill:active': {
      WebkitBoxShadow: '0 0 0 100px #fff inset',
    },
  },
}));

const StyledButton = muiStyled(Button)(({ theme }) => ({
  width: '100%',
}));

// Keep styled-components for non-MUI elements
const Description = styled.span`
  color: ${COLOR.black500};
  display: block;
  font-size: ${p => p.align ? '12px' : '14px'};
  font-weight: 400;
  line-height: 20px;
  text-align: ${p => p.align && 'center'};
`;

const Error = styled.div`
  background-color: ${lighten(0.45, 'red')};
  border: 1px solid ${lighten(0.2, 'red')};
  border-radius: 3px;
  color: red;
  font-size: 14px;
  font-weight: 500;
  padding: 8px;
  text-align: center;
  width: 100%;
`;

const Form = styled.form`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Header = styled.span`
  color: ${COLOR.black100};
  display: block;
  font-size: ${p => p.align ? '20px' : '24px'};
  font-weight: 700;
  line-height: 26px;
  margin-bottom: ${p => p.align ? '12px' : '16px'};
  text-align: ${p => p.align && 'center'};
`;

const Link = styled.button`
  background-color: transparent;
  border: none;
  color: ${COLOR.green100};
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  outline: none;
  transition: all 300ms ease;

  &:hover {
    text-decoration: underline;
  }
`;

const RegisterContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 24px;
`;

const SuccessContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SuccessDescription = styled.span`
  color: ${COLOR.black500};
  display: block;
  font-size: 13px;
  margin-bottom: 16px;
  text-align: center;
`;

const SuccessHeader = styled.span`
  color: ${COLOR.black100};
  display: block;
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const Text = styled.span`
  color: ${COLOR.black400};
  display: inline-block;
  font-size: 13px;
  margin-right: 3px;
`;

const RegistrationForm = ({ toggleRegistration }) => {
    const dispatch = useDispatch();
    const [usernameError, setUsernameError] = useState(false);
    const [errorCaracteres, setErrorCaracteres] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [formatPasswordError, setFormatPasswordError] = useState(false);
    const [data, setData] = useState({});
    const [showPassword, setShowPassword] = useState(false);

    const usernameValidation = (id, value) => {
        const validation = /^[0-9a-zA-Z]+$/;
        if(!value){
            setUsernameError(true);
        } else if (validation.test(value.trim())) {
            setErrorCaracteres(false);
            setUsernameError(false);
        } else {
            if(value.trim() !== ""){
                setUsernameError(false);
                setErrorCaracteres(true);
            }
        }
    };

    const handleChange = (id, value) => {
        let _value = value;
        _value = value?.trim()
        setData({
            ...data,
            [id]: _value
        });
        setFormatPasswordError(false);
        setError(null);
    };

    const validateForm = () => {
        if (!data.classCode || !data.classCode.trim()) {
            setError("Class code is required");
            return false;
        }

        if (!data.username || !data.username.trim()) {
            setUsernameError(true);
            setError("Username is required");
            return false;
        }

        const usernameRegex = /^[0-9a-zA-Z]+$/;
        if (!usernameRegex.test(data.username.trim())) {
            setErrorCaracteres(true);
            setError("Username must contain only alphanumeric characters");
            return false;
        }

        if (!verifyPassword(data.password)) {
            setFormatPasswordError(true);
            setError("Password does not meet requirements");
            return false;
        }

        return true;
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setError("");
        setFormatPasswordError(false);
        setUsernameError(false);
        setErrorCaracteres(false);

        if (!validateForm()) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const classCode = data.classCode.trim();
            const username = data.username.trim();

     

            const createStudent = await dispatch(handleAddStudentWithClass({
                classCode: classCode,
                username: username,
                password: get(data, 'password', ''),
                firstName: get(data, "firstName", ''),
                lastName: get(data, "lastName", ''),
                grade: get(data, "grade", ''),
                email: get(data, "email", '')
            }));
            setLoading(false);

            if (createStudent && !createStudent.error) {
                setSuccess('Student account successfully created.');
            } else {
                const errorCode = createStudent?.error_code;
                const usernameRegex = /^[0-9a-zA-Z]+$/;
                
                if (errorCode === "USERNAME_ALREADY_EXISTS") {
                    setError("Username already exists. Please choose a different username.");
                    setUsernameError(true);
                } else if (errorCode === "INVALID_USERNAME_OR_PASSWORD") {
                    setError("Username or password does not meet requirements. Please check your input.");
                    if (!usernameRegex.test(username)) {
                        setErrorCaracteres(true);
                    }
                    if (!verifyPassword(data.password)) {
                        setFormatPasswordError(true);
                    }
                } else if (errorCode === "CLASS_NOT_FOUND" || errorCode === "CLASS_ID_OR_ORG_ID_NOT_FOUND") {
                    setError("Class with that code doesn't exist, please contact your administrator");
                } else {
                    setError(createStudent?.message || 'There was an issue creating your account, please contact your administrator');
                }
            }
        } catch (error) {
            setLoading(false);
            setError('There was an issue creating your account, please contact your administrator');
        } finally {
            setLoading(false);
        }
    };

    function verifyPassword(password){
        var paswd = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,15}$/;
        if(!password){
                setFormatPasswordError(true);
                return false;
        }
        else if(password.match(paswd))
        {
            setFormatPasswordError(false);
            return true;
        }else
        {
            setFormatPasswordError(true);
            return false;
        }
    }

    return (
        <>
            {!success ? (
                <>
                    <Form onSubmit={handleSubmit} className='userRegForm' autoComplete="off">
                        <Row style={{ width: '100%' }}>
                            <Col span={24}>
                                <Header align="center">Register for a Class</Header>
                            </Col>
                        </Row>
                        <Row gutter={[0, 40]} style={{ width: '100%' }}>
                            <Col span={24}>
                                <Description align="center">You will need your class code, please get in contact with your teacher if you do not have this information.</Description>
                            </Col>
                        </Row>
                        {error && (
                            <Row gutter={[0, 24]} style={{ width: '100%' }}>
                                <Col span={24}>
                                    <Error>{error}</Error>
                                </Col>
                            </Row>
                        )}
                        <Row gutter={[8, 24]} style={{ width: '100%', marginTop: 10 }}>
                            <Col span={24} className="floatingLabelBG">
                                <StyledTextField
                                    autoComplete="off"
                                    id="username"
                                    defaultValue={' '}
                                    error={usernameError}
                                    onChange={e => handleChange(e.target.id, e.target.value)}
                                    label="Unique Username"
                                    onBlur={e => usernameValidation(e.target.id, e.target.value)}
                                    variant="outlined"
                                    value={data.username}
                                    helperText={usernameError && 'Username not available'}
                                    fullWidth
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Person />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Col>
                            {errorCaracteres && (
                                <FormHelperText error>Please user only alphanumeric characters.</FormHelperText>
                            )}
                        </Row>
                        <Row gutter={[8, 24]} style={{ width: '100%', marginTop: 10 }}>
                            <Col span={24} className="floatingLabelBG">
                                <StyledTextField
                                    autoComplete="off"
                                    id="classCode"
                                    defaultValue={' '}
                                    value={data.classCode}
                                    onChange={e => handleChange(e.target.id, e.target.value)}
                                    label="Class Code"
                                    variant="outlined"
                                    fullWidth
                                />
                            </Col>
                        </Row>
                        <Row gutter={[8, 24]} style={{ width: '100%', marginTop: 10 }}>
                            <Col span={24} className="floatingLabelBG">
                                <StyledTextField
                                    autoComplete="off"
                                    id="firstName"
                                    defaultValue={' '}
                                    value={data.firstName}
                                    onChange={e => handleChange(e.target.id, e.target.value)}
                                    label="First Name"
                                    variant="outlined"
                                    fullWidth
                                />
                            </Col>
                        </Row>
                        <Row className='userNameRow' gutter={[8, 24]}>
                            <Col xs={{ span: 24 }} md={{ span: 18 }} className="floatingLabelBG regLastName">
                                <StyledTextField
                                    autoComplete="off"
                                    id="lastName"
                                    defaultValue={' '}
                                    value={data.lastName}
                                    onChange={e => handleChange(e.target.id, e.target.value)}
                                    label="Last Name"
                                    variant="outlined"
                                    fullWidth
                                />
                            </Col>
                            <Col xs={{ span: 24 }} md={{ span: 6 }} className="floatingLabelBG regGrade">
                                <StyledTextField
                                    autoComplete="off"
                                    id="grade"
                                    defaultValue={' '}
                                    value={data.grade}
                                    onChange={e => handleChange(e.target.id, e.target.value)}
                                    label="Grade"
                                    variant="outlined"
                                    fullWidth
                                />
                            </Col>
                        </Row>
                        <Row gutter={[8, 24]} style={{ width: '100%', marginTop: 10 }}>
                            <Col span={24} className="floatingLabelBG">
                                <StyledTextField
                                    autoComplete="off"
                                    id="password"
                                    error={formatPasswordError}
                                    defaultValue={''}
                                    value={data.password}
                                    onChange={e => handleChange(e.target.id, e.target.value)}
                                    onBlur={e => verifyPassword(e.target.value)}
                                    label="Password"
                                    variant="outlined"
                                    type={showPassword ? "text" : "password"}
                                    fullWidth
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                        size="small"
                                                        sx={{
                                                            padding: '4px',
                                                            backgroundColor: 'transparent',
                                                            '&:hover': {
                                                                backgroundColor: 'transparent',
                                                            },
                                                        }}
                                                    >
                                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                                {formatPasswordError && (
                                    <FormHelperText error>Please use 8 to 15 characters which contain at least one lowercase letter, one uppercase letter, one numeric digit and one special character.</FormHelperText>
                                )}
                            </Col>
                        </Row>
                        <Row gutter={[8, 24]} style={{ width: '100%', marginTop: 10 }}>
                            <Col span={24}>
                                <StyledButton
                                    disabled={!(data.username && data.classCode && data.firstName && data.lastName && data.grade && data.password) || usernameError || errorCaracteres || loading || error}
                                    variant="contained"
                                    color="primary"
                                    type='submit'
                                >
                                    {loading ? <Loading size={20} white /> : 'Register'}
                                </StyledButton>
                            </Col>
                        </Row>
                    </Form>
                    <RegisterContainer>
                        <Text>Already registered for a class?</Text>
                        <Link onClick={toggleRegistration}>Login Now</Link>
                    </RegisterContainer>
                </>
            ) : (
                <SuccessContainer>
                    <CheckCircle style={{ color: COLOR.green100, fontSize: '128px', marginBottom: '24px' }} />
                    <SuccessHeader>Welcome {data.firstName} {data.lastName}</SuccessHeader>
                    <SuccessDescription>You have been successfully registered, click the button below to login and get started.</SuccessDescription>
                    <StyledButton
                        variant="contained"
                        color="primary"
                        onClick={toggleRegistration}
                    >
                        Login
                    </StyledButton>
                </SuccessContainer>
            )}
        </>
    );
};

export default RegistrationForm;